import { apiCall } from 'app/shared/lib/api/api';
import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, Sorting, SortDirection, IForm } from 'app/shared/lib/essette-site';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { deepClone } from 'app/shared/lib/utilities';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { EventAggregator } from 'aurelia-event-aggregator';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { I18N } from 'aurelia-i18n';
import { IPopup } from 'app/shared/elements/popup-base';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { LastEligibilityProductDeletedReloadEvent } from 'app/core/lib/events';
import { MemberService } from 'app/cd/services/member-service';
import { SaveEligibilityProduct } from 'app/core/components/member/save-eligibility-product';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { Toaster } from 'app/shared/lib/toaster';
import { validate } from 'app/shared/elements/ess-rule';
import * as CD from 'app/cd/models/essette-api';

@autoinject()
export class MemberEligibilityDetail extends ComponentBase {

    ////****************** Fields and Properties ************************************////

    @bindable
    protected memberPlanId: string | null = null;
    @bindable
    protected memberId: string | null = null;
    @bindable
    protected memberPlan: string | null = null;
    @bindable
    protected isMemberPlanActive: boolean = false;
    @bindable
    protected healthPlanId: string | null = null;
    @bindable
    protected lineOfBusinessId: string | null = null;
    @bindable
    protected includeInactive: boolean = false;

    protected eligibilities: CD.MemberEligibility[] = [];
    protected pageSize = 10;
    protected resultCount = 0;

    private sorting: Sorting = new Sorting();
    protected validateForm = validate;


    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $form!: IForm;
    protected $saveEligibilityProductModal!: IPopup;
    protected $saveEligibilityProduct!: SaveEligibilityProduct;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: MemberService,
        private readonly events: EventAggregator,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,

        ...rest: any[]) {

        super(...rest);
    }

    protected bind() {
        this.reload();
    }

    ////****************** Events ***************************************************////
    public async reload(): Promise<void> {
        await this.loadEligibilityDetails();
    }

    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    protected formSuccess({ detail }: FormSuccessCustomEvent) {
        this.$saveEligibilityProductModal.close();

        if(detail.form.reset) detail.form.reset();

        // reload grid and reset page
        this.reload();
    }

    ////****************** Functions ************************************************////
    protected saveEligibilityProduct(eligibility: CD.MemberEligibility): void {
        this.$saveEligibilityProduct.memberEligibility = deepClone(eligibility);
        if(this.healthPlanId && this.lineOfBusinessId) {
            //for loading Product search query based on healthPlan and LOB in context
            this.$saveEligibilityProduct.loadProduct(this.healthPlanId, this.lineOfBusinessId);
        }
        this.$saveEligibilityProductModal.open();
    }

    protected saveEligibilityProductClose(): void {
        if(this.$form.reset) this.$form.reset();
        this.$saveEligibilityProductModal.close();
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    private async loadEligibilityDetails(): Promise<void> {
        if(!this.memberId || !this.memberPlanId) return;
        const includes: string[] = [
            'product'
        ];
        const fields: string[] = [
            'dateRange.*',
            'employer',
            'isActive',
            'groupNumber',
            'product.description',
            'receivedDate',
            'shareOfCostAmount'
        ];
        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            sorting: this.sorting,
            page: this.$pager?.page || 0,
            includeInactive: this.includeInactive
        };

        try {
            this.state.beginLoad();

            const response = await this.service.loadMemberEligibilitiesByPlanId(this.memberId, this.memberPlanId, options, includes, fields);
            this.eligibilities = response.results || [];
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateMemberEligibility(eligibility: CD.MemberEligibility): Promise<void> {
        if(!eligibility?.id) return;

        let memberPlanToast = (this.eligibilities.filter(el => el.isActive).length === 1 && eligibility.isActive) || (this.eligibilities.filter(el => el.isActive).length === 0 || this.eligibilities === undefined); // Show eligibility plan toast if it's the last eligibility being deactivated

        // Show confirmation dialog only if deactivating
        if(eligibility.isActive && !memberPlanToast) {
            const result = await confirmation.showDeactivate('cd:member.eligibility.$', 1, false);
            if(!(result.dialogResponse === confirmation.options.OK)) return;
        }
        else if(eligibility.isActive && memberPlanToast) {
            const message = this.i18n.tr('ui:messages.actions.deactivate', { what: '$t(cd:member.eligibility.$)' });
            //TODO:: Uncomment and plug in subMessage for custom message in confirmation dialog for last eligibility deactivation
            //const subMessage = this.i18n.tr('ui:messages.warnings.deactivateLastEligibility', null);
            const result = await confirmation.showCustom(message, "", confirmation.options.StandardConfirm, false);
            if(!(result.dialogResponse === confirmation.options.OK)) return;
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateMemberPlanEligibility(eligibility.id, !eligibility.isActive);
            isSuccessful = response.isSuccessful;
        } catch(error) {
            throw error;
        } finally {
            if(isSuccessful) {
                this.events.publish(new LastEligibilityProductDeletedReloadEvent());
                await this.loadEligibilityDetails();
            }
            if(memberPlanToast && isSuccessful) {
                this.toaster.standard.activate('cd:member.plan.$long', !eligibility.isActive, isSuccessful);
            }
            this.toaster.standard.activate('cd:member.eligibility.$', !eligibility.isActive, isSuccessful);
            this.state.endSave();
        }
    }

}
